import { type ReactNode, createContext, useContext, useEffect, useMemo } from 'react'

const SOUND_LIBRARY = {
  kaching: '/sounds/kaching.mp3',
} as const

type SoundKey = keyof typeof SOUND_LIBRARY

const isBrowser = typeof window !== 'undefined'

class AudioPlayer {
  private audioContext: AudioContext | null = null
  private buffers = new Map<SoundKey, AudioBuffer>()
  private isInitialized = false

  constructor() {
    if (isBrowser) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    }
  }

  async initialize(): Promise<void> {
    if (!isBrowser || !this.audioContext || this.isInitialized) return

    try {
      const loadPromises = Object.entries(SOUND_LIBRARY).map(async ([key, url]: [string, string]) => {
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`Failed to fetch sound "${key}": ${response.statusText}`)
        }

        const arrayBuffer = await response.arrayBuffer()
        const buffer = await this.audioContext!.decodeAudioData(arrayBuffer)
        this.buffers.set(key as SoundKey, buffer)
      })

      await Promise.all(loadPromises)
      this.isInitialized = true
    } catch (error) {
      console.error('AudioPlayer initialization failed:', error)
    }
  }

  play(soundKey: SoundKey): void {
    if (!isBrowser || !this.audioContext) return

    const buffer = this.buffers.get(soundKey)

    if (!buffer) {
      console.warn(`Sound "${soundKey}" not loaded`)
      return
    }

    try {
      const source = this.audioContext.createBufferSource()
      source.buffer = buffer
      source.connect(this.audioContext.destination)
      const playSound = () => source.start(0)

      if (this.audioContext.state === 'suspended') {
        this.audioContext
          .resume()
          .then(playSound)
          .catch((error) => {
            console.error('Failed to resume audio context:', error)
          })
      } else {
        playSound()
      }
    } catch (error) {
      console.error(`Failed to play sound "${soundKey}":`, error)
    }
  }

  hasSound(soundKey: SoundKey): boolean {
    return this.buffers.has(soundKey)
  }
}

interface SoundContextValue {
  play: (soundKey: SoundKey) => void
  hasSound: (soundKey: SoundKey) => boolean
}

const SoundContext = createContext<SoundContextValue | null>(null)

interface SoundProviderProps {
  children: ReactNode
}

export function SoundProvider({ children }: SoundProviderProps) {
  const audioPlayer = useMemo(() => new AudioPlayer(), [])
  useEffect(() => {
    audioPlayer.initialize()
  }, [audioPlayer])
  const value = useMemo(
    () => ({
      play: (soundKey: SoundKey) => audioPlayer.play(soundKey),
      hasSound: (soundKey: SoundKey) => audioPlayer.hasSound(soundKey),
    }),
    [audioPlayer]
  )
  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>
}

export function useSound(): SoundContextValue {
  const context = useContext(SoundContext)
  if (!context) {
    throw new Error('useSound must be used within a SoundProvider')
  }
  return context
}
